import {FC, PropsWithChildren, useEffect, useMemo, useState} from "react";
import LoadingScreen from "components/ScreenState/Loading";
import ErrorState from "components/ScreenState/Error";
import CenterContainer from "components/CenterContainer";
import {useGetCurrenciesQuery} from "services/http/api/currency";
import {useGetCountriesQuery} from "services/http/api/countries";
import {ROLE_SUPER_ADMIN} from "src/constants";
import {BrandsContext} from "./BrandsContext";
import {CountriesContext} from "./CountriesContext";
import {CurrenciesContext} from "./CurrenciesContext";

import {useGetBrandsQuery} from "services/http/api/brand";
import {useAuthenticatedUser} from "../user";
import {setBrandId} from "../brand";

/**
 * AppSetupProvider is a unified provider that orchestrates all initial data fetching
 * for the application. It manages a single loading and error state to prevent UI
 * flashing caused by nested providers loading sequentially.
 */
export const AppSetupProvider: FC<PropsWithChildren> = ({children}) => {
  // Ensure the loading screen shows for at least a short period.
  const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);
  const user = useAuthenticatedUser();

  const isSuperAdmin = user.role === ROLE_SUPER_ADMIN;
  const {
    data: brands,
    loading: isBrandsLoading,
    error: brandsError,
  } = useGetBrandsQuery(undefined, {
    skip: !isSuperAdmin,
  });

  useEffect(() => {
    setBrandId(prevState => {
      if (prevState || !brands || brands.length === 0) return prevState;
      return brands[0].id; // Initialize to first brand in the list
    });
  }, [brands]);

  const {
    data: countries,
    loading: isCountriesLoading,
    error: countriesError,
  } = useGetCountriesQuery(); // Wait for user to fetch global data

  const {
    data: currencies,
    loading: isCurrenciesLoading,
    error: currenciesError,
  } = useGetCurrenciesQuery(); // Wait for user to fetch global data

  const brandsValue = useMemo(
    () => (isSuperAdmin ? brands : []),
    [isSuperAdmin, brands],
  );

  // Unified Loading State
  const isDataLoading =
    (isSuperAdmin && isBrandsLoading) ||
    isCountriesLoading ||
    isCurrenciesLoading;

  useEffect(() => {
    const timer = setTimeout(() => setMinLoadingTimeElapsed(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Show loading screen when these conditions are met:
  // 1. All data has finished loading.
  // 2. The minimum 500ms has elapsed.
  if (isDataLoading || !minLoadingTimeElapsed) {
    return (
      <CenterContainer>
        <LoadingScreen />
      </CenterContainer>
    );
  }

  const combinedError = brandsError || countriesError || currenciesError;

  if (combinedError) {
    return (
      <CenterContainer>
        <ErrorState
          error={combinedError}
          onRetry={() => window.location.reload()}
        />
      </CenterContainer>
    );
  }

  return (
    <BrandsContext.Provider value={brandsValue}>
      <CountriesContext.Provider value={countries}>
        <CurrenciesContext.Provider value={currencies}>
          {children}
        </CurrenciesContext.Provider>
      </CountriesContext.Provider>
    </BrandsContext.Provider>
  );
};
